import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthContext';

const ClientsContext = createContext();

export function ClientsProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);
  const refreshInFlightRef = useRef(false);
  const clientsRef = useRef(clients);

  useEffect(() => {
    clientsRef.current = clients;
  }, [clients]);

  const refreshClients = useCallback(async () => {
    if (refreshInFlightRef.current) {
      return clientsRef.current;
    }

    refreshInFlightRef.current = true;
    const token = localStorage.getItem('token');
    if (!token) {
      setClients([]);
      setUsers([]);
      setError('Missing access token');
      refreshInFlightRef.current = false;
      return [];
    }

    const clientCoreApiUrl = process.env.REACT_APP_CLIENT_CORE_API_URL || '';
    const companiesUrl = `${clientCoreApiUrl}/v1/clients/companies`;
    const usersUrl = `${clientCoreApiUrl}/v1/clients/users`;

    try {
      setLoading(true);
      setError(null);

      const [companiesRes, usersRes] = await Promise.all([
        fetch(companiesUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }),
        fetch(usersUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }),
      ]);

      const errors = [];

      if (companiesRes.ok) {
        const data = await companiesRes.json();
        setClients(Array.isArray(data) ? data : []);
      } else {
        setClients([]);
        errors.push(`Companies (${companiesRes.status})`);
      }

      if (usersRes.ok) {
        const data = await usersRes.json();
        setUsers(Array.isArray(data) ? data : []);
      } else {
        setUsers([]);
        errors.push(`Users (${usersRes.status})`);
      }

      if (errors.length) {
        setError(`Failed to load: ${errors.join(', ')}`);
      }

      return clientsRef.current;
    } catch (e) {
      console.error('Failed to load clients data:', e);
      setClients([]);
      setUsers([]);
      setError('Failed to load clients');
      return [];
    } finally {
      setLoading(false);
      refreshInFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (authLoading) {
      return;
    }
    if (!user) {
      setClients([]);
      setUsers([]);
      setError(null);
      return;
    }
    refreshClients();
  }, [user, authLoading, refreshClients]);

  return (
    <ClientsContext.Provider value={{ clients, users, refreshClients, loading, error }}>
      {children}
    </ClientsContext.Provider>
  );
}

export function useClients() {
  return useContext(ClientsContext);
}
